/*
 * 18Gimmies web API client.
 *
 * Mirrors the mobile API client (apps/mobile/src/api/client.ts) so the same
 * endpoints, the same JWT refresh dance, and the same shape of responses are
 * available on the web. The only differences:
 *   - Token storage uses our localStorage wrapper (`storage`) instead of
 *     expo-secure-store.
 *   - There are no React Native–only imports.
 *
 * We start with the auth surface and grow the endpoint list as web screens
 * are ported. Don't duplicate ALL mobile endpoints up front — add them when
 * the screen that needs them is being built.
 */

import type {
  AuthResponse,
  CourseDetailsResponse,
  CourseImportRequest,
  CourseImportResponse,
  MeResponse,
  RegisterRequest,
  EventListItem,
  TournamentEvent,
  TournamentByCodePreview,
  TournamentTeam,
  EventPlayer,
  TournamentActivityLogEntry,
  CreateTournamentRequest,
  AddGuestPlayerRequest,
  InvitePlayerRequest,
  TournamentRound,
  TournamentRoundListItem,
  TournamentMatch,
  CreateRoundRequest,
  DraftState,
  EventLeaderboard,
  EventRecap,
  TournamentPlayerStat,
  MatchLiveState,
  MatchScoringPayload,
  PendingTournamentInvitationsResponse,
  TournamentInvitationCodeResponse,
  StartScorecardRoundRequest,
  StartScorecardRoundResponse,
  ScorecardHoleScoreInput,
  SaveScorecardHoleResponse,
  ScorecardResponse,
  TeamStablefordPayload,
} from '@golf-app/shared';

import { createApiRequester } from './apiRequest';
import { useAuthStore } from '@/store/authStore';

export const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  'https://api-production-8f99.up.railway.app';

// Re-export commonly-used types so callers can do
//   import { api, type MeResponse } from '@/lib/api'
// without crossing the shared package boundary themselves.
export type { AuthResponse, MeResponse } from '@golf-app/shared';

export const request = createApiRequester({
  baseUrl: BASE_URL,
  getSession: useAuthStore.getState,
  fetch: (...args) => fetch(...args),
});

// ── Auth endpoints ─────────────────────────────────────────────────────────
// All request/response shapes live in `@golf-app/shared/src/api-types.ts`.
// If the API contract changes, update that file first — TypeScript will then
// flag every call site that's still reading the old shape.
export const api = {
  login: (username: string, password: string) =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }, false),

  register: (body: RegisterRequest) =>
    request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }, false),

  logout: (refreshToken: string) =>
    request<{ ok: true }>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }, false),

  // ── Password reset (6-digit code via transactional email) ───────────────
  // forgotPassword: always returns success even for unknown users
  // (prevents user-enumeration). resetPassword: 400 with .error on bad code.
  forgotPassword: (identifier: string) =>
    request<{ success: true }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    }),
  resetPassword: (identifier: string, code: string, newPassword: string) =>
    request<{ success: true }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ identifier, code, newPassword }),
    }),

  me: () => request<MeResponse>('/v1/me'),
  updateMe: (body: Partial<{ displayName: string; firstName: string; lastName: string; phone: string; email: string }>) =>
    request<MeResponse>('/v1/me', { method: 'PATCH', body: JSON.stringify(body) }),
  /** Upload a profile photo as a data URL (data:image/...;base64,XXXX).
   *  The backend extracts the base64 + mime parts internally. */
  uploadAvatar: (dataUrl: string) => {
    const m = /^data:(image\/[a-z+]+);base64,(.+)$/i.exec(dataUrl);
    if (!m) return Promise.reject(new Error('Invalid image data'));
    return request<{ avatarUrl: string }>('/v1/me/avatar', {
      method: 'PUT',
      body: JSON.stringify({ mimeType: m[1], base64: m[2] }),
    });
  },
  removeAvatar: () => request<{ avatarUrl: null }>('/v1/me/avatar', { method: 'DELETE' }),

  // ── Tournaments: events ──────────────────────────────────────────────────
  getTournaments: () => request<EventListItem[]>('/tournaments'),
  getTournament: (eventId: string) =>
    request<TournamentEvent>(`/tournaments/${eventId}`),

  // Public preview by invite code. Auth optional.
  getTournamentByCode: (code: string) =>
    request<TournamentByCodePreview>(`/tournaments/by-code/${code}`),
  // Auth-required join. If claimEventPlayerId is set, the user merges into
  // that unclaimed guest slot. Otherwise a new event_player is created.
  // Response includes unclaimedGuests for the "is this you?" picker.
  joinTournamentByCode: (code: string, claimEventPlayerId?: string) =>
    request<TournamentEvent & { unclaimedGuests?: EventPlayer[]; claimed?: boolean }>(
      `/tournaments/by-code/${code}/join`,
      {
        method: 'POST',
        body: JSON.stringify(claimEventPlayerId ? { claimEventPlayerId } : {}),
      },
    ),
  createTournament: (body: CreateTournamentRequest) =>
    request<TournamentEvent>('/tournaments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTournament: (eventId: string, body: Record<string, unknown>) =>
    request<TournamentEvent>(`/tournaments/${eventId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // Permanent delete. Owner only. Cascades through everything
  // (teams, players, rounds, matches, holes, invites, activity).
  deleteTournament: (eventId: string) =>
    request<{ ok: true; deletedEventId: string }>(`/tournaments/${eventId}`, {
      method: 'DELETE',
    }),
  archiveTournament: (eventId: string) =>
    request<TournamentEvent>(`/tournaments/${eventId}/archive`, {
      method: 'POST',
      body: '{}',
    }),

  // ── Tournaments: teams ───────────────────────────────────────────────────
  createTournamentTeam: (eventId: string, body: Record<string, unknown>) =>
    request<TournamentTeam>(`/tournaments/${eventId}/teams`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTournamentTeam: (teamId: string, body: Record<string, unknown>) =>
    request<TournamentTeam>(`/tournament-teams/${teamId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  assignTournamentCaptain: (teamId: string, eventPlayerId: string) =>
    request<TournamentTeam>(`/tournament-teams/${teamId}/captain`, {
      method: 'POST',
      body: JSON.stringify({ eventPlayerId }),
    }),
  removeTournamentCaptain: (teamId: string) =>
    request<TournamentTeam>(`/tournament-teams/${teamId}/captain`, {
      method: 'DELETE',
    }),

  // ── Tournaments: roster ──────────────────────────────────────────────────
  getTournamentPlayers: (eventId: string) =>
    request<EventPlayer[]>(`/tournaments/${eventId}/players`),
  addTournamentGuest: (eventId: string, body: AddGuestPlayerRequest) =>
    request<EventPlayer>(`/tournaments/${eventId}/players/guest`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  inviteTournamentPlayer: (eventId: string, body: InvitePlayerRequest) =>
    request<EventPlayer & { claimToken?: string }>(
      `/tournaments/${eventId}/players/invite`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateTournamentPlayer: (
    eventPlayerId: string,
    body: Record<string, unknown>,
  ) =>
    request<EventPlayer>(`/event-players/${eventPlayerId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  removeTournamentPlayer: (eventPlayerId: string) =>
    request<{ success: boolean }>(`/event-players/${eventPlayerId}`, {
      method: 'DELETE',
      body: '{}',
    }),
  // Personal claim link for an unclaimed slot — same URL the invite email
  // carries. Owner/captain only; 409 if the player already joined.
  getPlayerClaimLink: (eventPlayerId: string) =>
    request<{ url: string; displayName: string | null }>(`/event-players/${eventPlayerId}/claim-link`, {
      method: 'POST',
      body: '{}',
    }),
  // Detach the linked account from a claimed slot (wrong-person claim
  // fix). Owner only. Slot keeps name/handicap/team/matches.
  unclaimEventPlayer: (eventPlayerId: string) =>
    request<EventPlayer>(`/event-players/${eventPlayerId}/unclaim`, {
      method: 'POST',
      body: '{}',
    }),
  // Captain/owner override of a match result. Skips hole-by-hole entry
  // and finalizes the match with the given resultType + optional reason.
  // Backend enforces owner/captain role.
  adminOverrideMatch: (
    matchId: string,
    body: { resultType: 'team_a_win' | 'team_b_win' | 'tie'; display?: string; reason?: string },
  ) =>
    request<unknown>(`/tournament-matches/${matchId}/admin-override`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Claim a specific unclaimed event_player slot using the token issued in
  // the invite email. Stronger than join-by-code because it pins the user
  // to the exact slot the captain seeded for them — name, team, handicap,
  // payment handles, all carry over without a "is this you?" picker.
  claimEventPlayer: (eventPlayerId: string, claimToken: string) =>
    request<EventPlayer>(`/event-players/${eventPlayerId}/claim`, {
      method: 'POST',
      body: JSON.stringify({ claimToken }),
    }),

  getPendingTournamentInvitations: () =>
    request<PendingTournamentInvitationsResponse>('/event-invitations/pending'),
  requestTournamentInvitationCode: (invitationId: string) =>
    request<TournamentInvitationCodeResponse>(
      `/event-invitations/${invitationId}/request-code`,
      { method: 'POST', body: '{}' },
    ),
  acceptTournamentInvitation: (invitationId: string, code: string) =>
    request<EventPlayer>(`/event-invitations/${invitationId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  // ── Tournaments: activity feed ───────────────────────────────────────────
  getTournamentActivity: (eventId: string) =>
    request<TournamentActivityLogEntry[]>(`/tournaments/${eventId}/activity`),

  // ── App-user search (used by invite-by-username flow) ────────────────────
  // The API returns rows shaped { userId, username, displayName } (see
  // /auth/search). The previous type-claim of { id, ... } was wrong and
  // silently broke AddTournamentPlayer's app-user path — picked.id was
  // undefined, the request fell through to email/phone and 400'd.
  searchUsers: (q: string) =>
    request<{ userId: string; username: string; displayName: string | null }[]>(
      `/auth/search?q=${encodeURIComponent(q)}`,
    ),

  // ── Courses (for round course/tee picker) ────────────────────────────────
  //
  // Two different searches:
  //   searchCourses     → local DB only. Fast, but only finds courses we've
  //                       already imported. Used as a "recently imported"
  //                       quick path.
  //   searchCoursesAPI  → proxies to GolfCourseAPI catalog. Finds anything,
  //                       including courses we've never seen. Used when the
  //                       local search misses (which is most of the time
  //                       for a fresh DB).
  //   importCourse      → persists an external API course (+ its tees and
  //                       holes) into the local DB. Idempotent: dedups by
  //                       lower(name)+city+state. Returns the real DB
  //                       courseId + tee IDs that round creation needs.
  //
  // Matches the round-flow CourseScreen on mobile.
  searchCourses: (query: string, signal?: AbortSignal) =>
    request<{ id: string; name: string; city: string | null; state: string | null }[]>(
      `/courses?query=${encodeURIComponent(query)}`, { signal },
    ),
  searchCoursesAPI: (q: string, signal?: AbortSignal) =>
    request<{
      courses?: Array<{
        id: number | string;
        club_name?: string;
        course_name?: string;
        location?: { city?: string; state?: string };
        tees?: {
          male?: Array<{
            tee_name: string;
            slope_rating: number;
            course_rating: number;
            par_total: number;
            holes?: Array<{ par: number; handicap: number }>;
          }>;
          female?: Array<{
            tee_name: string;
            slope_rating: number;
            course_rating: number;
            par_total: number;
            holes?: Array<{ par: number; handicap: number }>;
          }>;
        };
      }>;
    }>(`/courses/search?q=${encodeURIComponent(q)}`, { signal }),
  importCourse: (data: CourseImportRequest) =>
    request<CourseImportResponse>(
      '/courses/import', { method: 'POST', body: JSON.stringify(data) },
    ),
  getCourse: (id: string, signal?: AbortSignal) =>
    request<CourseDetailsResponse>(`/courses/${id}`, { signal }),

  // ── Core scorecards ────────────────────────────────────────────────────
  // Casual scorecards are separate from Team Cup tournament rounds. This is
  // the API surface used by Stableford and regular hole-by-hole scoring.
  startScorecardRound: (body: StartScorecardRoundRequest) =>
    request<StartScorecardRoundResponse>('/scorecards/start', {
      method: 'POST', body: JSON.stringify(body),
    }),
  getScorecard: (roundId: string) =>
    request<ScorecardResponse>(`/rounds/${roundId}/scorecard`),
  saveScorecardHole: (
    roundId: string,
    holeNumber: number,
    scores: ScorecardHoleScoreInput[],
  ) =>
    request<SaveScorecardHoleResponse>(`/rounds/${roundId}/scorecard/hole`, {
      method: 'POST', body: JSON.stringify({ holeNumber, scores }),
    }),
  completeScorecardRound: (roundId: string) =>
    request<ScorecardResponse>(`/rounds/${roundId}/scorecard/complete`, {
      method: 'POST', body: '{}',
    }),

  // ── Tournament rounds + lifecycle ────────────────────────────────────────
  getTournamentRounds: (eventId: string) =>
    request<TournamentRoundListItem[]>(`/tournaments/${eventId}/rounds`),
  getTournamentRound: (roundId: string) =>
    request<TournamentRound>(`/tournament-rounds/${roundId}`),
  createTournamentRound: (eventId: string, body: CreateRoundRequest) =>
    request<TournamentRound>(`/tournaments/${eventId}/rounds`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateTournamentRound: (roundId: string, body: Record<string, unknown>) =>
    request<TournamentRound>(`/tournament-rounds/${roundId}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  publishTournamentRound: (roundId: string) =>
    request<TournamentRound>(`/tournament-rounds/${roundId}/publish`, { method: 'POST', body: '{}' }),
  lockTournamentRound: (roundId: string) =>
    request<TournamentRound>(`/tournament-rounds/${roundId}/lock`, { method: 'POST', body: '{}' }),
  unlockTournamentRound: (roundId: string) =>
    request<TournamentRound>(`/tournament-rounds/${roundId}/unlock`, { method: 'POST', body: '{}' }),
  startTournamentRound: (roundId: string) =>
    request<TournamentRound>(`/tournament-rounds/${roundId}/start`, { method: 'POST', body: '{}' }),
  completeTournamentRound: (roundId: string) =>
    request<TournamentRound>(`/tournament-rounds/${roundId}/complete`, { method: 'POST', body: '{}' }),

  // ── Team-Cup Stableford ────────────────────────────────────────────────
  getTeamStableford: (roundId: string) =>
    request<TeamStablefordPayload>(`/tournament-rounds/${roundId}/stableford`),
  setupTeamStableford: (roundId: string) =>
    request<TeamStablefordPayload>(`/tournament-rounds/${roundId}/stableford/setup`, {
      method: 'POST', body: '{}',
    }),
  saveTeamStablefordHole: (
    roundId: string,
    holeNumber: number,
    scores: Array<{ eventPlayerId: string; grossScore: number }>,
  ) => request<TeamStablefordPayload>(
    `/tournament-rounds/${roundId}/stableford/holes/${holeNumber}/scores`,
    { method: 'POST', body: JSON.stringify({ scores }) },
  ),

  // ── Tournament matches + participants ────────────────────────────────────
  createTournamentMatch: (roundId: string, body: Record<string, unknown>) =>
    request<TournamentMatch>(`/tournament-rounds/${roundId}/matches`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  getTournamentMatch: (matchId: string) =>
    request<TournamentMatch>(`/tournament-matches/${matchId}`),
  updateTournamentMatch: (matchId: string, body: Record<string, unknown>) =>
    request<TournamentMatch>(`/tournament-matches/${matchId}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  deleteTournamentMatch: (matchId: string) =>
    request<{ ok: boolean }>(`/tournament-matches/${matchId}`, { method: 'DELETE', body: '{}' }),
  addMatchParticipant: (matchId: string, body: Record<string, unknown>) =>
    request<TournamentMatch>(`/tournament-matches/${matchId}/participants`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  removeMatchParticipant: (participantId: string) =>
    request<TournamentMatch>(`/match-participants/${participantId}`, {
      method: 'DELETE', body: '{}',
    }),
  computeMatchHandicaps: (matchId: string) =>
    request<unknown>(`/tournament-matches/${matchId}/compute-handicaps`, { method: 'POST', body: '{}' }),

  // ── Draft + auto-balance ─────────────────────────────────────────────────
  getDraftRoom: (eventId: string) =>
    request<DraftState>(`/tournaments/${eventId}/draft-room`),
  createDraftRoom: (eventId: string, body: Record<string, unknown> = {}) =>
    request<DraftState>(`/tournaments/${eventId}/draft-room`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  startDraft: (roomId: string) =>
    request<DraftState>(`/draft-rooms/${roomId}/start`, { method: 'POST', body: '{}' }),
  draftPick: (roomId: string, selectedEventPlayerId: string) =>
    request<DraftState>(`/draft-rooms/${roomId}/pick`, {
      method: 'POST', body: JSON.stringify({ selectedEventPlayerId }),
    }),
  undoDraftPick: (roomId: string) =>
    request<DraftState>(`/draft-rooms/${roomId}/undo`, { method: 'POST', body: '{}' }),
  pauseDraft: (roomId: string) =>
    request<DraftState>(`/draft-rooms/${roomId}/pause`, { method: 'POST', body: '{}' }),
  resumeDraft: (roomId: string) =>
    request<DraftState>(`/draft-rooms/${roomId}/resume`, { method: 'POST', body: '{}' }),
  finalizeDraft: (roomId: string) =>
    request<DraftState>(`/draft-rooms/${roomId}/finalize`, { method: 'POST', body: '{}' }),
  autoBalanceTeams: (eventId: string, apply: boolean) =>
    request<{ assignment: Record<string, string>; teamHandicapTotals: Record<string, number>; applied: boolean }>(
      `/tournaments/${eventId}/auto-balance`, { method: 'POST', body: JSON.stringify({ apply }) },
    ),

  // ── Leaderboard + Recap + Stats ──────────────────────────────────────────
  getEventLeaderboard: (eventId: string) =>
    request<EventLeaderboard>(`/tournaments/${eventId}/leaderboard`),
  getEventRecap: (eventId: string) =>
    request<EventRecap>(`/tournaments/${eventId}/recap`),
  getEventStats: (eventId: string) =>
    request<{ players: TournamentPlayerStat[]; teams: TournamentTeam[] }>(`/tournaments/${eventId}/stats`),

  // ── Match result override (captain / owner) ──────────────────────────────
  //
  // Forces a match to a specific final result. Used when a captain needs
  // to correct an entered score, accept a concession, or otherwise fix a
  // result that was confirmed wrong.
  overrideMatchResult: (
    matchId: string,
    opts: { resultType: 'team_a_win' | 'team_b_win' | 'tie'; display?: string; reason?: string },
  ) =>
    request<any>(`/tournament-matches/${matchId}/admin-override`, {
      method: 'POST',
      body: JSON.stringify(opts),
    }),
  reopenMatchResult: (matchId: string) =>
    request<any>(`/tournament-matches/${matchId}/reopen-result`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /** Submit the result calculated from saved hole scores. A resultType is only
   *  accepted for a configured manual-result match scorer. Owners and captains
   *  use the admin override endpoint for declarations and corrections. */
  submitMatchResult: (
    matchId: string,
    opts?: { resultType?: 'team_a_win' | 'team_b_win' | 'tie'; display?: string },
  ) =>
    request<any>(`/tournament-matches/${matchId}/submit-result`, {
      method: 'POST',
      body: JSON.stringify(opts ?? {}),
    }),

  // ── My Matches + Score Entry ─────────────────────────────────────────────
  //
  // Endpoints are intentionally loose-typed for now (`unknown`) — they
  // return a wide bundle that the score-entry page consumes. Once the
  // shape stabilizes we can drop them into packages/shared.
  getMyMatches: (eventId: string) =>
    request<{ matches: any[] }>(`/tournaments/${eventId}/my-matches`),
  getMatchScoring: (matchId: string) =>
    request<MatchScoringPayload>(`/tournament-matches/${matchId}/scoring`),
  postHoleScores: (
    matchId: string,
    holeNumber: number,
    scores: { eventPlayerId: string; grossScore: number }[],
  ) =>
    request<MatchLiveState>(`/tournament-matches/${matchId}/holes/${holeNumber}/scores`, {
      method: 'POST',
      body: JSON.stringify({ scores }),
    }),
  quickHoleResult: (
    matchId: string,
    holeNumber: number,
    result: 'team_a' | 'team_b' | 'halved',
  ) =>
    request<MatchLiveState>(`/tournament-matches/${matchId}/holes/${holeNumber}/result`, {
      method: 'POST',
      body: JSON.stringify({ result }),
    }),

  // ── Convenience: anything else lives here as it gets needed. ─────────────
  // Future ports go below this line as screens come online.
};
